/**
 * Auth provider registry.
 *
 * @see .grizzly/kb/sop-auth-provider-architecture.md
 */

import type { AuthProvider } from './types.js'
import { GoogleAuthProvider } from './providers/google.js'

const REGISTRY: Record<string, new () => AuthProvider> = {
  google: GoogleAuthProvider,
}

/**
 * AUTH_PROVIDER is inlined at build time by esbuild (see PUBLIC_ENV_KEYS), so
 * this is a literal in the bundle rather than a runtime environment read.
 *
 * Google is the only provider. GitHub was removed once the CLI moved to Google
 * — it was the last consumer, and until then a "harmless" unused provider was
 * quietly the reason `grizzly auth login` could not authenticate at all.
 */
const DEFAULT_PROVIDER = 'google'

export function getConfiguredProvider(): AuthProvider {
  // NO `typeof process` guard. esbuild's `define` replaces the member
  // expression `process.env.AUTH_PROVIDER` with a literal at build time, but
  // never defines `process` itself — so a runtime guard is FALSE in the browser
  // and silently discards the inlined value. That is exactly why a
  // Google-configured build kept rendering "Sign in with GitHub".
  // `??` alone would be wrong here: esbuild inlines an unset variable as '',
  // which is not nullish, so DEFAULT_PROVIDER would never apply.
  const configured = process.env.AUTH_PROVIDER ?? ''
  const name = configured === '' ? DEFAULT_PROVIDER : configured
  const Ctor = REGISTRY[name]
  if (!Ctor) {
    throw new Error(
      `Unknown auth provider "${name}". Available: ${Object.keys(REGISTRY).join(', ')}`,
    )
  }
  return new Ctor()
}
